// `extension/extension.state.tabs.config.ng1.ts:196` — the abstract `extension-tabs` state's
// template, which is the chrome every `/extension/**` screen renders inside (plan §7 phase 3,
// slice 1).
//
// Upstream's shape, and every part of it is load-bearing:
//
//   <div class="woo-extension woo-extension-tabs">
//     <div ui-view="extension-header" class="woo-extension-header-view" ng-class="handle-right-aligned">
//       <shortlist-header extension-product="…">
//     <div class="extension-tabs" ng-class="handle-right-aligned | tabs-disabled">
//       <div class="extension-tabs-indicators-wrap" ng-if="!hideTabIndicators">
//         <ul class="extension-tabs-indicators">
//           <li class="extension-tab-indicator-wrap" ui-sref-active="active">
//             <a class="extension-tab-indicator">
//       <div ui-view="extension-tabs-content-view" class="extension-tabs-content-view">
//
// ⚠️ THE `ui-view` ELEMENTS ARE DIVS HERE AND THAT IS NOT THE USUAL CASE. Everywhere else in
// this port the ui-view is its own unknown element, because upstream writes `<ui-view>` and
// the inline box it produces is what percentage heights resolve through (see AuthLayout's
// note). Here upstream writes `<div ui-view="…" class="…">` — a named view as an ATTRIBUTE on
// a div that already carries the layout class — so the box is a block one, and reproducing it
// as an element would change the flex children of `.woo-extension`. Read the template, not
// the convention.
import type { ReactElement, ReactNode } from 'react'

/** `ExtensionTabGroupIcons` — Font Awesome classes, verbatim from the service. */
export const ExtensionTabGroupIcons = {
  hunters: 'fa fa-solid fa-search',
  candidate: 'fa fa-solid fa-user',
  clientContactHunters: 'fa fa-solid fa-search',
  clientContact: 'fa fa-solid fa-user',
} as const

export type ExtensionTabGroup = keyof typeof ExtensionTabGroupIcons

/**
 * `ShortlistGroupOrder` and `ClientContactGroupOrder` (extension-shortlist-tabs.service.ng1.ts:28, 38)
 * — the strip's order, which upstream states explicitly for each product.
 *
 * ⚠️ IT IS NOT THE ALPHABETICAL ORDER, and sorting by the group STRING gets both products
 * backwards. `candidate` sorts before `hunters`, and `clientContact` before
 * `clientContactHunters`, while upstream puts the hunters tab first in each pair — so a
 * `localeCompare` reverses the strip the moment a second tab exists.
 */
export const EXTENSION_TAB_GROUP_ORDER: Record<ExtensionTabGroup, number> = {
  hunters: 0,
  candidate: 1,
  clientContactHunters: 0,
  clientContact: 1,
}

/**
 * Which PRODUCT each group belongs to.
 *
 * ⚠️ THE STRIP IS PER PRODUCT, AND THE TWO ORDERS ABOVE ARE THE PROOF. `ShortlistGroupOrder`
 * and `ClientContactGroupOrder` are separate tables that BOTH start at 0 — they could not be
 * one strip, because two groups would share a position. Upstream keeps a tab list per product
 * and shows the current one; a port that accumulates across products ends up with two tabs
 * both reading "SEARCHES" and both carrying the magnifier, which is what the contacts screen
 * showed beside build 354's single tab.
 */
export const EXTENSION_TAB_GROUP_PRODUCT: Record<ExtensionTabGroup, 'candidates' | 'clientContacts'> = {
  hunters: 'candidates',
  candidate: 'candidates',
  clientContactHunters: 'clientContacts',
  clientContact: 'clientContacts',
}

/**
 * `closeableGroups` (extension-shortlist-tabs.service.ng1.ts:610) — which tabs show the ×.
 *
 * ⚠️ IT IS A PROPERTY OF THE GROUP, NOT OF THE TAB. The two ENTITY groups close and the two
 * HUNTER groups do not, in both products — a searches tab is the frame's home and has nothing
 * to fall back to. Deriving it per tab, or defaulting it on, puts a × on a tab that cannot be
 * closed.
 */
export const CLOSEABLE_TAB_GROUPS: Record<ExtensionTabGroup, boolean> = {
  hunters: false,
  candidate: true,
  clientContactHunters: false,
  clientContact: true,
}

/** `IShortlistTab`, minus the parts that only the tab SERVICE needs (Phase 3 slice 2). */
export interface ExtensionTab {
  group: ExtensionTabGroup
  /** The label, uppercased by CSS rather than here — `text-transform: uppercase`. */
  text: string
  /** Where the indicator navigates. A path here; upstream stores a ui-router state name. */
  href: string
  /** Upstream renders the × only for a tab that declares itself closeable. */
  isCloseable?: boolean
}

export interface ExtensionTabsProps {
  tabs: readonly ExtensionTab[]
  /** Which tab is `ui-sref-active="active"`. */
  activeGroup?: ExtensionTabGroup
  /** `data.hideTabIndicators` on the entered state — the whole strip goes, not just its rows. */
  hideTabIndicators?: boolean
  /** `!tabsEnabled` — dims and blocks the strip while a modal flow owns the frame. */
  tabsEnabled?: boolean
  /**
   * `extCtrl.isRTL` — NOT a language direction. It is `ShortListAttachTo.Left !==
   * handleLocation.attachTo`: the panel dragged to the right-hand edge of the host page. The
   * SPA asks the content script for it (`useIsRtl`), and the class it adds here is what hides
   * the header's pin in the injected panel.
   */
  isRTL?: boolean
  /** `<shortlist-header>`, passed in rather than built here: the header has its own props. */
  header: ReactNode
  /** What the named `extension-tabs-content-view` renders — the route's own outlet. */
  children?: ReactNode
  onCloseTab?: (group: ExtensionTabGroup) => void
  onSelectTab?: (tab: ExtensionTab) => void
}

export function ExtensionTabs({
  tabs,
  activeGroup,
  hideTabIndicators = false,
  tabsEnabled = true,
  isRTL = false,
  header,
  children,
  onCloseTab,
  onSelectTab,
}: ExtensionTabsProps): ReactElement {
  return (
    <div className="woo-extension woo-extension-tabs">
      <div className={`woo-extension-header-view${isRTL ? ' handle-right-aligned' : ''}`}>{header}</div>
      <div
        className={['extension-tabs', isRTL ? 'handle-right-aligned' : '', tabsEnabled ? '' : 'tabs-disabled']
          .filter(Boolean)
          .join(' ')}
      >
        {hideTabIndicators ? null : (
          <div className="extension-tabs-indicators-wrap">
            <ul className="extension-tabs-indicators">
              {tabs.map((tab) => (
                // `shrink-on-overflow` is upstream's directive for clamping a long alert name
                // into the strip; the clamping itself is `vertical-line-clamp()` on
                // `.extension-tab-indicator-text`, which is CSS and already applies. The
                // directive's measuring half belongs with the tab service in slice 2.
                <li
                  key={tab.group}
                  className={`extension-tab-indicator-wrap${tab.group === activeGroup ? ' active' : ''}`}
                >
                  <a
                    className="extension-tab-indicator"
                    href={tab.href}
                    onClick={(event) => {
                      if (onSelectTab === undefined) return
                      event.preventDefault()
                      onSelectTab(tab)
                    }}
                  >
                    {/* Upstream writes `<i … />` self-closed, which HTML does not honour —
                        the browser nests everything after it INSIDE the <i>. React closes it
                        properly, so the text and close CTA become siblings as the stylesheet
                        assumes (`.extension-tab-indicator-text` and `-close` are both
                        children of `.extension-tab-indicator`). Pixel-identical; the DOM is
                        the one upstream's CSS was written for rather than the one its HTML
                        accidentally produces. */}
                    <i className={`extension-tab-indicator-icon ${ExtensionTabGroupIcons[tab.group]}`} />
                    <span className="extension-tab-indicator-text" title={tab.text}>
                      {tab.text}
                    </span>
                    {tab.isCloseable === true ? (
                      <i
                        className="fas fa-times extension-tab-indicator-close"
                        role="button"
                        aria-label={`Close ${tab.text}`}
                        tabIndex={0}
                        onClick={(event) => {
                          // Upstream passes `$event` and stops it reaching the `ui-sref`, so
                          // closing a tab does not also navigate to it.
                          event.preventDefault()
                          event.stopPropagation()
                          onCloseTab?.(tab.group)
                        }}
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter' && event.key !== ' ') return
                          event.preventDefault()
                          event.stopPropagation()
                          onCloseTab?.(tab.group)
                        }}
                      />
                    ) : null}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="extension-tabs-content-view">{children}</div>
      </div>
    </div>
  )
}
